import type { ReactElement } from "react";
import {
  DragDropContext,
  Draggable,
  Droppable,
  type DraggableProvided,
  type DropResult,
} from "react-beautiful-dnd";
import { reorderWithDragResult } from "../utils/reorderWithDragResult";

export type ReorderableVerticalListRender<T> = (
  item: T,
  index: number,
  provided: DraggableProvided,
) => ReactElement | null;

export type ReorderableVerticalListProps<T> = {
  items: readonly T[];
  getId: (item: T) => string;
  onReorder: (next: T[]) => void;
  /** Must be unique among droppables that share the same DragDropContext (one per list instance). */
  droppableId: string;
  isDragDisabled?: boolean;
  className?: string;
  children: ReorderableVerticalListRender<T>;
};

/**
 * Vertical reorder list using react-beautiful-dnd (same stack as content management).
 * Each instance owns a {@link DragDropContext} so lists stay isolated.
 */
export default function ReorderableVerticalList<T>({
  items,
  getId,
  onReorder,
  droppableId,
  isDragDisabled = false,
  className,
  children,
}: ReorderableVerticalListProps<T>) {
  const handleDragEnd = (result: DropResult) => {
    const next = reorderWithDragResult(items, result);
    if (next) onReorder(next);
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId={droppableId} direction="vertical">
        {(droppableProvided) => (
          <div
            {...droppableProvided.droppableProps}
            ref={droppableProvided.innerRef}
            className={className}
          >
            {items.map((item, index) => (
              <Draggable
                key={getId(item)}
                draggableId={getId(item)}
                index={index}
                isDragDisabled={isDragDisabled}
              >
                {(draggableProvided) =>
                  children(item, index, draggableProvided)
                }
              </Draggable>
            ))}
            {droppableProvided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
}
